import PolicyShell, { Section, SubHead } from "@/components/PolicyShell";

const STANDARD_ROWS = [
  ["XS", "XS", "40\"", "38\"", "17.5\"", "25.0\""],
  ["S", "S", "42\"", "40\"", "18.0\"", "25.5\""],
  ["M", "M", "44\"", "42\"", "18.5\"", "26.0\""],
  ["L", "L", "46\"", "44\"", "19.5\"", "26.5\""],
  ["XL", "XL", "48\"", "46\"", "20.5\"", "27.0\""],
  ["XXL", "XXL", "51\"", "49\"", "21.5\"", "27.5\""],
  ["XXXL", "3XL", "54\"", "52\"", "22.5\"", "28.0\""],
];

const SHEARLING_ROWS = [
  ["XS", "XS", "44\"", "42\"", "18.5\"", "25.5\""],
  ["S", "S", "46\"", "44\"", "19.0\"", "26.0\""],
  ["M", "M", "48\"", "46\"", "19.5\"", "26.5\""],
  ["L", "L", "50\"", "48\"", "20.5\"", "27.0\""],
  ["XL", "XL", "53\"", "51\"", "21.5\"", "27.5\""],
  ["XXL", "XXL", "56\"", "54\"", "22.5\"", "28.0\""],
  ["XXXL", "3XL", "59\"", "57\"", "23.5\"", "28.5\""],
];

const HEADER = [
  "Brand Size",
  "US Size",
  "Chest (Jacket)",
  "Bottom / Hem",
  "Shoulder",
  "Sleeve Length",
];

const SizeTable = ({ rows, testId, headers = HEADER }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-sm" data-testid={testId}>
      <thead className="text-[#c9a84c]">
        <tr className="border-b border-[#000000]/15">
          {headers.map((h) => (
            <th key={h} className="py-3 pr-4 font-medium uppercase tracking-[0.16em] text-[11px]">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-[#ffffff]/10">
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j} className="py-3 pr-4 text-[#000000]/85">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function SizeGuidePage() {
  return (
    <PolicyShell
      testId="size-guide-page"
      overline="BOSCAGE"
      title="Size Guide & Fit Advisor"
      contact="Email your stats to hello@boscage.com for a tailor's recommendation"
    >
      <p>
        Finding the perfect fit for a leather jacket is essential. At BOSCAGE Elegance, we use Finished Garment
        Measurements rather than body measurements. Because different styles require different amounts of ease
        (loosing), measuring a jacket you already own is the most accurate way to find your BOSCAGE size.
      </p>

      <Section title="The Foolproof Way to Get Your Perfect Size">
        <p>Not sure which size to pick? Let our Master Tailors decide for you!</p>
        <p>
          Before placing your order, we highly recommend emailing your exact body measurements (Height, Weight, Chest
          circumference, and Belly/Waist circumference) to hello@boscage.com. Our experts will analyze your
          proportions and recommend the absolute best fit for your chosen style.
        </p>
      </Section>

      <Section title="How to Measure for the Perfect Fit">
        <p>
          Do not measure your body. Instead, grab your favorite fitting jacket, lay it flat on a table, zip it up, and
          use a measuring tape.
        </p>
        <ul className="space-y-2 pl-2">
          <li className="flex gap-3">
            <span className="text-[#000000] shrink-0">◆</span>
            <span>
              <strong className="text-[#000000]">Chest:</strong> Measure straight across from armpit to armpit, then
              double the number.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-[#000000] shrink-0">◆</span>
            <span>
              <strong className="text-[#000000]">Shoulder:</strong> Measure from the top shoulder seam straight across
              to the other shoulder seam.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-[#000000] shrink-0">◆</span>
            <span>
              <strong className="text-[#000000]">Sleeve:</strong> Measure from the top shoulder seam down to the cuff
              edge.
            </span>
          </li>
          <li className="flex gap-3">
            <span className="text-[#000000] shrink-0">◆</span>
            <span>
              <strong className="text-[#000000]">Bottom / Hem (Crucial for Belly Fit):</strong> Measure straight across
              the very bottom of the jacket, then double the number.
            </span>
          </li>
        </ul>
      </Section>

      <Section title="Chart 1 — Standard Leather & Biker Jackets">
        <p className="italic text-[#000000]/65">(Garment Measurements)</p>
        <p>
          Use this chart for classic bikers, bombers, suede, and blazers. These feature a standard 2-to-4 inches of
          ease for a tailored fit.
        </p>
        <SizeTable rows={STANDARD_ROWS} testId="standard-size-table" />
      </Section>

      <Section title="Chart 2 — Real Shearling & Heavy Winter Coats">
        <p className="italic text-[#000000]/65">(Garment Measurements)</p>
        <p>
          Genuine shearling jackets feature thick, natural fur interiors. Our tailors add 6-to-8 inches of ease to the
          external chest measurement to accommodate the thick lining. The outer measurements below are larger, but the
          inside fit remains true to size.
        </p>
        <SizeTable
          rows={SHEARLING_ROWS}
          testId="shearling-size-table"
          headers={[
            "Brand Size",
            "US Size",
            "Chest (Outer)",
            "Bottom / Hem",
            "Shoulder",
            "Sleeve Length",
          ]}
        />
      </Section>

      <Section title="Pro-Tips from Our Master Tailors">
        <SubHead>1. The &ldquo;Belly Fat&rdquo; Rule</SubHead>
        <p>
          If your belly/midsection measurement is larger than your chest, always rely on the &lsquo;Bottom/Hem&rsquo;
          measurement in our size charts. A jacket must be able to zip up comfortably at the waist.
        </p>
        <SubHead>2. The Biker Snug Fit</SubHead>
        <p>
          Classic Moto and Biker jackets are designed to fit snugly. If you have broad shoulders, love handles, or
          prefer wearing thick hoodies underneath your standard leather jacket, please consider ordering one size up
          from Chart 1.
        </p>
        <p className="text-sm text-[#000000]/70 italic pt-2">
          Still Unsure? Email your body stats to hello@boscage.com and let our styling team guide you to the perfect
          fit!
        </p>
      </Section>
    </PolicyShell>
  );
}
